import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useDebouncedCallback } from 'use-debounce';
import { Plus, X, Move, ChevronDown, LayoutGrid } from 'lucide-react';
import { DataTable } from "../../components/data-table";
import { SiteHeader } from "../../components/site-header";
import { useFilters } from "@/common/Components/SideBar/Layout";
import axiosInstance from "@/api/axios";
import { WasteComposition } from "@/components/waste-composition";
import { BinMediaViewer } from "@/components/bin-images-viewer";
import { DashboardExport } from "@/components/DashboardExport";
import { useTranslation } from 'react-i18next';
import {
    WasteStreamProduction,
    Stats,
    BinUsageRanking,
    FillLevelConsistency,
    PeakUsageTime,
    PeakFillLevel,
} from "../../components";
import SubclassDisplay from "../../components/Subclass-Display/SubclassDisplay";
import ContaminationChart from "../../components/ContaminationChart";
import WasteCreated from "@/pages/Others/Components/WasteCreated";
import { initializeApp } from '@firebase/app';
import { getMessaging, getToken, onMessage } from '@firebase/messaging';
import SubclassCardView from "../../components/Subclass-Display/SubclassCardView";
import { ContaminationRateChart } from "../../components/ContaminationRateChart";
import { ContaminationTime } from "../../components/Contaminationtime";
import { WasteForecast } from "../../components/Wasteforecast";

// Circularity imports
import CO2Chart from "@/pages/CO2savings/co2.jsx";
import RecyclabilityScore from '@/pages/Recycle/RecyclabilityScore.jsx';
import { RecyclingChart } from '@/pages/Recycle/recycling-chart.jsx';
import TreesEquivalent from '../Recycle/TreesEquivalent';
import Costsavingbarchart from '../costsavings/Costsavingbars.jsx';
import ComplianceCostSavingsCard from '../costsavings/ComplianceSavingsCard';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

const firebaseConfig = {
    apiKey: "AIzaSyBylDQErfG_E2F7LpO6boLYf4QMRwtek3g",
    authDomain: "plaex-notifications-8dfa0.firebaseapp.com",
    projectId: "plaex-notifications-8dfa0",
    storageBucket: "plaex-notifications-8dfa0.firebasestorage.app",
    messagingSenderId: "16520628768",
    appId: "1:16520628768:web:f3f43ec41ebaa3ba8f8a45"
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

const ADMIN_EMAILS = new Set([
    "tyrellpantophlet@gmail.com",
    "itsplaex@gmail.com",
]);

const GRID_STORAGE_KEY = 'home_dashboard_grid_v1';

const getUserStorageKey = (key) => {
    const userKey = localStorage.getItem("userKey") || "default";
    return `plaex_${userKey}_${key}`;
};

// ─── Chart category definitions ───────────────────────────────────────────────

const OPERATIONS_CHART_IDS = [
    'waste_created',
    'stats',
    'subclass_card',
    'subclass_display',
    'waste_stream',
    'peak_usage',
    'fill_consistency',
    'bin_ranking',
    'waste_composition',
    'data_table',
    'contamination_chart',
    'contamination_rate',
    'contamination_time',
    // 'waste_forecast',
    'bin_media',
];

const CIRCULARITY_CHART_IDS = [
    'co2chart',
    'recyclability_score',
    'recycling_rate',
    'trees_equivalent',
    'cost_savings_bar',
    'compliance_cost',
];

const CHART_META = {
    // Operations
    waste_created:       { title: 'Waste Created',           category: 'Operations', defaultW: 4, defaultH: 3 },
    stats:               { title: 'Waste Generation Stats',  category: 'Operations', defaultW: 8, defaultH: 3 },
    subclass_card:       { title: 'Subclass Card View',      category: 'Operations', defaultW: 8, defaultH: 6 },
    subclass_display:    { title: 'Subclass Display',        category: 'Operations', defaultW: 8, defaultH: 3 },
    waste_stream:        { title: 'Waste Stream Production', category: 'Operations', defaultW: 8, defaultH: 3 },
    peak_usage:          { title: 'Peak Usage Time',         category: 'Operations', defaultW: 8, defaultH: 3 },
    fill_consistency:    { title: 'Fill Level Consistency',  category: 'Operations', defaultW: 8, defaultH: 3 },
    bin_ranking:         { title: 'Bin Usage Ranking',       category: 'Operations', defaultW: 8, defaultH: 3 },
    waste_composition:   { title: 'Waste Composition',       category: 'Operations', defaultW: 8, defaultH: 3 },
    data_table:          { title: 'Fill Level Table',        category: 'Operations', defaultW: 4, defaultH: 3 },
    contamination_chart: { title: 'Contamination Chart',     category: 'Operations', defaultW: 4, defaultH: 3 },
    contamination_rate:  { title: 'Contamination Rate',      category: 'Operations', defaultW: 8, defaultH: 3 },
    contamination_time:  { title: 'Contamination Time',      category: 'Operations', defaultW: 8, defaultH: 3 },
    bin_media:           { title: 'Bin Media Viewer',        category: 'Operations', defaultW: 8, defaultH: 3 },
    // Circularity
    co2chart:            { title: 'CO2 Emissions',           category: 'Circularity', defaultW: 6, defaultH: 3 },
    recyclability_score: { title: 'Recyclability Score',     category: 'Circularity', defaultW: 6, defaultH: 3 },
    recycling_rate:      { title: 'Recycling Rate',          category: 'Circularity', defaultW: 6, defaultH: 3 },
    trees_equivalent:    { title: 'Trees Equivalent',        category: 'Circularity', defaultW: 6, defaultH: 3 },
    cost_savings_bar:    { title: 'Cost Savings Bar Chart',  category: 'Circularity', defaultW: 6, defaultH: 3 },
    compliance_cost:     { title: 'Compliance Cost Savings', category: 'Circularity', defaultW: 6, defaultH: 3 },
};

// Default layout on first load — mirrors the original Operations page order
const DEFAULT_ACTIVE_CHARTS = [
    'contamination_chart',
    'waste_created',
    'data_table',
    'stats',
    'subclass_card',
    'subclass_display',
    'waste_stream',
    'peak_usage',
    'fill_consistency',
    'bin_ranking',
    'waste_composition',
    'contamination_rate',
    'contamination_time',
];

const buildDefaultLayouts = (chartIds) => {
    const layouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };
    // Replicate the original 3-col left sidebar + 4-col main grid
    let leftY = 0;
    let mainY = 0;
    const leftIds = ['contamination_chart', 'waste_created', 'data_table'];

    chartIds.forEach((id) => {
        const meta = CHART_META[id] || { defaultW: 6, defaultH: 3 };
        if (leftIds.includes(id)) {
            layouts.lg.push({ i: id, x: 0,  y: leftY, w: 4,  h: meta.defaultH, minW: 2, minH: 2 });
            layouts.md.push({ i: id, x: 0,  y: leftY, w: 4,  h: meta.defaultH, minW: 2, minH: 2 });
            layouts.sm.push({ i: id, x: 0,  y: leftY, w: 6,  h: meta.defaultH, minW: 2, minH: 2 });
            layouts.xs.push({ i: id, x: 0,  y: leftY, w: 4,  h: meta.defaultH, minW: 2, minH: 2 });
            layouts.xxs.push({ i: id, x: 0, y: leftY, w: 2,  h: meta.defaultH, minW: 2, minH: 2 });
            leftY += meta.defaultH;
        } else {
            layouts.lg.push({ i: id, x: 4,  y: mainY, w: meta.defaultW, h: meta.defaultH, minW: 2, minH: 2 });
            layouts.md.push({ i: id, x: 4,  y: mainY, w: 6,              h: meta.defaultH, minW: 2, minH: 2 });
            layouts.sm.push({ i: id, x: 0,  y: mainY, w: 6,              h: meta.defaultH, minW: 2, minH: 2 });
            layouts.xs.push({ i: id, x: 0,  y: mainY, w: 4,              h: meta.defaultH, minW: 2, minH: 2 });
            layouts.xxs.push({ i: id, x: 0, y: mainY, w: 2,              h: meta.defaultH, minW: 2, minH: 2 });
            mainY += meta.defaultH;
        }
    });
    return layouts;
};

// ─── Persistent bin selection hook ────────────────────────────────────────────
const usePersistentBinSelection = () => {
    const storageKey = getUserStorageKey('selected_bins');
    const [selectedBins, setSelectedBins] = useState(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch (e) {}
        return [];
    });

    useEffect(() => {
        if (selectedBins.length === 0) return;
        try { localStorage.setItem(storageKey, JSON.stringify(selectedBins)); } catch (e) {}
    }, [selectedBins, storageKey]);

    const toggleBinSelection = (binId) =>
        setSelectedBins(prev => prev.includes(binId) ? prev.filter(id => id !== binId) : [...prev, binId]);
    const clearAllBins = () => setSelectedBins([]);
    const selectAllBins = (allBinIds) => setSelectedBins(allBinIds);
    const setDefaultBinsIfEmpty = (defaultBins) =>
        setSelectedBins(prev => prev.length > 0 ? prev : defaultBins);

    return { selectedBins, toggleBinSelection, clearAllBins, selectAllBins, setDefaultBinsIfEmpty };
};

// ─── Main Page Component ───────────────────────────────────────────────────────
export default function Page() {
    const {
        selectedBins: persistentSelectedBins,
        toggleBinSelection: persistentToggleBinSelection,
        clearAllBins: persistentClearAllBins,
        selectAllBins: persistentSelectAllBins,
        setDefaultBinsIfEmpty,
    } = usePersistentBinSelection();

    const [userRoleState, setUserRoleState] = useState(null);
    const [isLoadingRole, setIsLoadingRole] = useState(true);
    const [hasBinImages, setHasBinImages] = useState(false);
    const [binUsageIds, setBinUsageIds] = useState(null);
    const [showBinUsageStats, setShowBinUsageStats] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [refreshKey, setRefreshKey] = useState(0);
    const [localTimeRangeDropdownOpen, setLocalTimeRangeDropdownOpen] = useState(false);
    const [localBinDropdownOpen, setLocalBinDropdownOpen] = useState(false);
    const [currentUserEmail, setCurrentUserEmail] = useState(() => localStorage.getItem("userKey") ?? "");

    const [localSelectedTimeRange, setLocalSelectedTimeRange] = useState(() => {
        try {
            const saved = localStorage.getItem(getUserStorageKey('time_range'));
            return saved ? JSON.parse(saved) : "Year";
        } catch { return "Year"; }
    });

    const [localCustomDateRange, setLocalCustomDateRange] = useState(() => {
        try {
            const saved = localStorage.getItem(getUserStorageKey('custom_date'));
            if (saved) {
                const parsed = JSON.parse(saved);
                return [{ startDate: parsed.startDate ? new Date(parsed.startDate) : null, endDate: parsed.endDate ? new Date(parsed.endDate) : null, key: 'selection' }];
            }
        } catch {}
        return [{ startDate: null, endDate: null, key: 'selection' }];
    });

    const [localIsCustomDateRange, setLocalIsCustomDateRange] = useState(() => {
        try {
            const saved = localStorage.getItem(getUserStorageKey('custom_date'));
            if (saved) return JSON.parse(saved).isCustom || false;
        } catch {}
        return false;
    });

    const [localAllBins, setLocalAllBins] = useState([]);
    const [localWasteTypeDropdownOpen, setLocalWasteTypeDropdownOpen] = useState(false);
    const [localSelectedWasteTypes, setLocalSelectedWasteTypes] = useState(['organic']);
    const [wasteCreatedKg, setWasteCreatedKg] = useState(0);
    const [binTypes, setBinTypes] = useState({});
    const [hasGarbyVisionBins, setHasGarbyVisionBins] = useState(false);
    const hasInitializedBins = useRef(false);

    // ─── Grid / Chart state ────────────────────────────────────────────────────
    const [activeCharts, setActiveCharts] = useState(() => {
        try {
            const saved = localStorage.getItem(GRID_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.activeCharts?.length) return parsed.activeCharts;
            }
        } catch {}
        return DEFAULT_ACTIVE_CHARTS;
    });

    const [gridLayouts, setGridLayouts] = useState(() => {
        try {
            const saved = localStorage.getItem(GRID_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.layouts?.lg?.length) return parsed.layouts;
            }
        } catch {}
        return buildDefaultLayouts(DEFAULT_ACTIVE_CHARTS);
    });

    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [addMenuCategory, setAddMenuCategory] = useState('Operations');
    const [editMode, setEditMode] = useState(false);
    const addMenuRef = useRef(null);

    // Save grid to localStorage
    useEffect(() => {
        localStorage.setItem(GRID_STORAGE_KEY, JSON.stringify({ activeCharts, layouts: gridLayouts }));
    }, [activeCharts, gridLayouts]);

    // Close add menu on outside click
    useEffect(() => {
        const handler = (e) => {
            if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setAddMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        selectedTimeRange: contextTimeRange,
        selectedBins: contextBins,
        userRole,
        selectedWasteTypes: contextWasteTypes,
        wasteTypeOptions: contextWasteTypeOptions,
        customDateRange: contextCustomDateRange,
        isCustomDateRange: contextIsCustomDateRange,
    } = useFilters();

    const selectedBins = contextBins || persistentSelectedBins;
    const selectedTimeRange = contextTimeRange || localSelectedTimeRange;
    const timeRangeOptions = ["Day", "Week", "Month", "3 Month", "6 Month", "Year"];
    const selectedWasteTypes = contextWasteTypes || localSelectedWasteTypes;
    const wasteTypeOptions = contextWasteTypeOptions || ['pmd', 'organic', 'paper', 'residual'];
    const customDateRange = contextCustomDateRange || localCustomDateRange;
    const isCustomDateRange = contextIsCustomDateRange || localIsCustomDateRange;

    const timeRangeNorm = (tr) =>
        tr.toLowerCase().replace(/\s+/g, "") === "3month" ? "3months"
        : tr.toLowerCase().replace(/\s+/g, "") === "6month" ? "6months"
        : tr.toLowerCase().replace(/\s+/g, "");

    // ─── Existing data fetching effects (unchanged) ────────────────────────────
    useEffect(() => {
        if (currentUserEmail) return;
        axiosInstance.get("current-user/").then((res) => {
            const email = res.data?.email ?? "";
            setCurrentUserEmail(email);
            if (email) localStorage.setItem("userEmail", email);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!contextTimeRange) localStorage.setItem(getUserStorageKey('time_range'), JSON.stringify(localSelectedTimeRange));
    }, [localSelectedTimeRange, contextTimeRange]);

    useEffect(() => {
        if (!contextCustomDateRange) {
            const toSave = {
                startDate: localCustomDateRange[0]?.startDate instanceof Date ? localCustomDateRange[0].startDate.toISOString() : localCustomDateRange[0]?.startDate || null,
                endDate: localCustomDateRange[0]?.endDate instanceof Date ? localCustomDateRange[0].endDate.toISOString() : localCustomDateRange[0]?.endDate || null,
                isCustom: localIsCustomDateRange
            };
            localStorage.setItem(getUserStorageKey('custom_date'), JSON.stringify(toSave));
        }
    }, [localCustomDateRange, localIsCustomDateRange, contextCustomDateRange]);

    const handleCustomDateRangeChange = (newDateRange, isCustom) => {
        setLocalCustomDateRange(newDateRange);
        setLocalIsCustomDateRange(isCustom);
        if (!isCustom) localStorage.removeItem(getUserStorageKey('custom_date'));
    };

    const fetchBinTypes = async (bins) => {
        if (!bins || bins.length === 0) { setHasGarbyVisionBins(false); setBinTypes({}); return; }
        try {
            const response = await axiosInstance.get('/get_bins');
            const allBins = response.data;
            const binTypeMap = {};
            let garbyVisionFound = false;
            bins.forEach(binId => {
                const bin = allBins.find(b => b.unique_identifier === binId);
                if (bin) {
                    binTypeMap[binId] = { isGarbyVision: bin.is_garby_vision, isGarfill: bin.is_garfill, isWeightScale: bin.is_weight_scale };
                    if (bin.is_garby_vision) garbyVisionFound = true;
                }
            });
            setBinTypes(binTypeMap);
            setHasGarbyVisionBins(garbyVisionFound);
        } catch { setHasGarbyVisionBins(false); setBinTypes({}); }
    };

    useEffect(() => { fetchBinTypes(selectedBins); }, [selectedBins]);

    useEffect(() => {
        const savedRole = localStorage.getItem("userRole");
        if (savedRole) { setUserRoleState(savedRole); setIsLoadingRole(false); }
    }, []);

    useEffect(() => {
        const initializeNotifications = async () => {
            try {
                if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') return;
                const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                const currentToken = await getToken(messaging, { serviceWorkerRegistration: swRegistration });
                if (currentToken) await sendTokenToServer(currentToken);
            } catch (error) { console.error('Error initializing notifications:', error); }
        };
        initializeNotifications();
    }, [navigate]);

    const sendTokenToServer = async (token) => {
        try {
            const csrfResponse = await axiosInstance.get("csrf/");
            const csrfToken = csrfResponse.data.csrfToken;
            await axiosInstance.post('/fcm/devices/', { registration_id: token, type: 'web' }, {
                headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
                withCredentials: true,
            });
        } catch (error) { console.error('Error sending FCM token:', error); }
    };

    useEffect(() => {
        if (userRole) {
            setUserRoleState(userRole);
            localStorage.setItem("userRole", userRole);
            setIsLoadingRole(false);
        } else if (!userRoleState) {
            const fetchUserRole = async () => {
                try {
                    const response = await axiosInstance.get("usertier");
                    setUserRoleState(response.data);
                    localStorage.setItem("userRole", response.data);
                } catch { } finally { setIsLoadingRole(false); }
            };
            fetchUserRole();
        }
    }, [userRole, userRoleState]);

    useEffect(() => {
        const checkForBinImages = async () => {
            if (selectedBins.length > 0) {
                try {
                    const requests = selectedBins.map(binId => axiosInstance.get(`/bin-media/?bin_id=${binId}`));
                    const responses = await Promise.all(requests);
                    setHasBinImages(responses.some(r => r.data.data && r.data.data.length > 0));
                } catch { setHasBinImages(false); }
            } else { setHasBinImages(false); }
        };
        checkForBinImages();
    }, [selectedBins]);

    useEffect(() => {
        if (!contextBins && !hasInitializedBins.current) {
            hasInitializedBins.current = true;
            const fetchBinIds = async () => {
                try {
                    const response = await axiosInstance.get("bins-with-status");
                    const binsWithStatus = response.data || [];
                    const validBinIds = binsWithStatus.map(bin => bin.id).filter(id => id && typeof id === "string");
                    setLocalAllBins(binsWithStatus);
                    setDefaultBinsIfEmpty(validBinIds.slice(0, Math.min(3, validBinIds.length)));
                } catch {
                    try {
                        const fallbackResponse = await axiosInstance.get("bin_ids");
                        const binIds = (fallbackResponse.data || []).filter(id => id && typeof id === "string");
                        setLocalAllBins(binIds.map(id => ({ id, status: 'offline' })));
                        setDefaultBinsIfEmpty(binIds.slice(0, Math.min(3, binIds.length)));
                    } catch { }
                }
            };
            fetchBinIds();
        }
    }, [contextBins]);

    useEffect(() => {
        const fetchBinUsageIds = async () => {
            try {
                const queryParams = selectedBins.length > 0 ? `?bins=${selectedBins.join(',')}` : '';
                const response = await axiosInstance.get(`bin_usage_ids${queryParams}`, { withCredentials: true });
                const nonGarbyVisionBins = response.data || [];
                setBinUsageIds(nonGarbyVisionBins);
                setShowBinUsageStats(nonGarbyVisionBins.length > 0);
            } catch { setShowBinUsageStats(false); setBinUsageIds([]); }
        };
        if (selectedBins.length > 0) fetchBinUsageIds();
        else { setShowBinUsageStats(false); setBinUsageIds([]); }
    }, [selectedBins]);

    useEffect(() => {
        const refreshBinStatuses = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                setLocalAllBins(response.data || []);
            } catch { }
        };
        const interval = setInterval(refreshBinStatuses, 60000);
        return () => clearInterval(interval);
    }, []);

    const selectLocalTimeRange = (timeRange) => { setLocalSelectedTimeRange(timeRange); return false; };
    const toggleLocalWasteTypeSelection = (wasteType) => {
        setLocalSelectedWasteTypes(prev => prev.includes(wasteType) ? prev.filter(item => item !== wasteType) : [...prev, wasteType]);
        return false;
    };

    // ─── Grid chart management ─────────────────────────────────────────────────
    const addChart = useCallback((chartId) => {
        if (activeCharts.includes(chartId)) return;
        const meta = CHART_META[chartId] || { defaultW: 6, defaultH: 3 };
        const newLayout = (cols, w) => ({ i: chartId, x: 0, y: Infinity, w: Math.min(w, cols), h: meta.defaultH, minW: 2, minH: 2 });

        setActiveCharts(prev => [...prev, chartId]);
        setGridLayouts(prev => ({
            lg:  [...(prev.lg  || []), newLayout(12, meta.defaultW)],
            md:  [...(prev.md  || []), newLayout(10, meta.defaultW)],
            sm:  [...(prev.sm  || []), newLayout(6,  meta.defaultW)],
            xs:  [...(prev.xs  || []), newLayout(4,  meta.defaultW)],
            xxs: [...(prev.xxs || []), newLayout(2,  2)],
        }));
        setAddMenuOpen(false);
    }, [activeCharts]);

    const removeChart = useCallback((chartId) => {
        setActiveCharts(prev => prev.filter(id => id !== chartId));
        setGridLayouts(prev => ({
            lg:  (prev.lg  || []).filter(l => l.i !== chartId),
            md:  (prev.md  || []).filter(l => l.i !== chartId),
            sm:  (prev.sm  || []).filter(l => l.i !== chartId),
            xs:  (prev.xs  || []).filter(l => l.i !== chartId),
            xxs: (prev.xxs || []).filter(l => l.i !== chartId),
        }));
    }, []);

    const resetLayout = () => {
        setActiveCharts(DEFAULT_ACTIVE_CHARTS);
        setGridLayouts(buildDefaultLayouts(DEFAULT_ACTIVE_CHARTS));
    };

    const onLayoutChange = useDebouncedCallback((currentLayout, allLayouts) => {
        setGridLayouts(prev => {
            const sanitized = {};
            Object.keys(allLayouts).forEach(size => {
                sanitized[size] = allLayouts[size].map(l => ({
                    i: l.i, x: l.x, y: l.y, w: l.w, h: l.h, minW: 2, minH: 2
                }));
            });
            return sanitized;
        });
    }, 150);

    // ─── Render a chart by id ─────────────────────────────────────────────────
    const isAdmin = ADMIN_EMAILS.has((currentUserEmail ?? "").toLowerCase());

    const renderChart = (chartId) => {
        const sharedProps = {
            timeRange: selectedTimeRange,
            bins: selectedBins,
            customDateRange,
            isCustomDateRange,
        };

        switch (chartId) {
            case 'contamination_chart':
                return selectedBins.length > 0 && hasGarbyVisionBins ? (
                    <ContaminationChart
                        title={t('contamination.title')}
                        timeRange={timeRangeNorm(selectedTimeRange)}
                        bins={selectedBins}
                        customDateRange={customDateRange}
                        isCustomDateRange={isCustomDateRange}
                    />
                ) : <PlaceholderMsg msg="Requires GarbyVision bins" />;

            case 'waste_created':
                return (
                    <WasteCreated
                        bins={selectedBins}
                        timeRange={selectedTimeRange}
                        onKgWasteUpdate={setWasteCreatedKg}
                        customDateRange={customDateRange}
                        isCustomDateRange={isCustomDateRange}
                    />
                );

            case 'data_table':
                return (
                    <div className="relative w-full overflow-auto h-full">
                        <DataTable className="w-full caption-bottom text-sm" title={t("filllevellabel")} />
                    </div>
                );

            case 'stats':
                return (
                    <Stats
                        title={t("wastegenerationlabel")}
                        {...sharedProps}
                    />
                );

            case 'subclass_card':
                return selectedBins.length > 0 ? (
                    <SubclassCardView
                        className="w-full h-full"
                        id={selectedBins}
                        dayvalue={selectedTimeRange.toLowerCase()}
                        datevalue={customDateRange}
                        isCustomDateRange={isCustomDateRange}
                        wasteweight={wasteCreatedKg}
                        totalwasteproduceddata={1004}
                    />
                ) : <PlaceholderMsg msg="Select bins to view" />;

            case 'subclass_display':
                return selectedBins.length > 0 ? (
                    <SubclassDisplay
                        id={selectedBins}
                        currentWasteItem={selectedWasteTypes[0] || 'organic'}
                        currentWasteItem_={selectedWasteTypes[0] || 'organic'}
                        dayvalue={selectedTimeRange.toLowerCase()}
                        selectedButton={selectedTimeRange.toLowerCase()}
                        datevalue={customDateRange}
                        wasteweight={wasteCreatedKg}
                        isCustomDateRange={isCustomDateRange}
                    />
                ) : <PlaceholderMsg msg="Select bins to view" />;

            case 'bin_media':
                return selectedBins.length > 0 && hasBinImages ? (
                    <BinMediaViewer key={refreshKey} selectedBin={selectedBins} />
                ) : <PlaceholderMsg msg="No bin images available" />;

            case 'waste_stream':
                return <WasteStreamProduction title={t("wastestreamproductionlabel")} {...sharedProps} />;

            case 'peak_usage':
                return <PeakUsageTime title={t("peakUsageTime")} {...sharedProps} />;

            case 'fill_consistency':
                return <FillLevelConsistency title={t("fillLevelConsistency.title")} {...sharedProps} />;

            case 'bin_ranking':
                return <BinUsageRanking title={t("binUsageRanking.defaultTitle")} {...sharedProps} />;

            case 'waste_composition':
                return <WasteComposition title={t("wastecompositionlabel")} {...sharedProps} />;

            case 'contamination_rate':
                return selectedBins.length > 0 && hasGarbyVisionBins ? (
                    <ContaminationRateChart
                        title={t('contaminationRateChart.defaultTitle')}
                        {...sharedProps}
                    />
                ) : <PlaceholderMsg msg="Requires GarbyVision bins" />;

            case 'contamination_time':
                return selectedBins.length > 0 && hasGarbyVisionBins && isAdmin ? (
                    <ContaminationTime
                        title="Contamination Time"
                        {...sharedProps}
                        userEmail={currentUserEmail}
                    />
                ) : <PlaceholderMsg msg={isAdmin ? "Requires GarbyVision bins" : "Admin only"} />;

            // Circularity charts
            case 'co2chart':           return <CO2Chart />;
            case 'recyclability_score': return <RecyclabilityScore />;
            case 'recycling_rate':     return <RecyclingChart />;
            case 'trees_equivalent':   return <TreesEquivalent />;
            case 'cost_savings_bar':   return <Costsavingbarchart />;
            case 'compliance_cost':    return <ComplianceCostSavingsCard />;

            default: return <PlaceholderMsg msg={`Unknown chart: ${chartId}`} />;
        }
    };

    const availableToAdd = [...OPERATIONS_CHART_IDS, ...CIRCULARITY_CHART_IDS].filter(
        id => !activeCharts.includes(id)
    );
    const filteredAvailable = availableToAdd.filter(id => CHART_META[id]?.category === addMenuCategory);

    return (
        <DashboardExport>
            <div className="export-wrapper">
                <div className="tab-content h-full">
                    <div className="w-full max-w-[1920px] sm:px-4 m-auto sm:pt-0">

                        {/* Site Header */}
                        {!contextTimeRange && (
                            <div className="sticky top-16 pt-4 pb-6 text-right z-10 w-full ml-auto px-4 sm:pr-0 bg-gradient-to-t from-white/0 to-white dark:from-slate-950/0 dark:to-slate-950 flex gap-2 justify-end">
                                <SiteHeader
                                    timeRangeDropdownOpen={localTimeRangeDropdownOpen}
                                    setTimeRangeDropdownOpen={setLocalTimeRangeDropdownOpen}
                                    binDropdownOpen={localBinDropdownOpen}
                                    setBinDropdownOpen={setLocalBinDropdownOpen}
                                    selectedTimeRange={localSelectedTimeRange}
                                    selectTimeRange={selectLocalTimeRange}
                                    timeRangeOptions={timeRangeOptions}
                                    selectedBins={selectedBins}
                                    allBins={localAllBins}
                                    toggleBinSelection={persistentToggleBinSelection}
                                    wasteTypeDropdownOpen={localWasteTypeDropdownOpen}
                                    setWasteTypeDropdownOpen={setLocalWasteTypeDropdownOpen}
                                    selectedWasteTypes={localSelectedWasteTypes}
                                    toggleWasteTypeSelection={toggleLocalWasteTypeSelection}
                                    wasteTypeOptions={wasteTypeOptions}
                                    customDateRange={localCustomDateRange}
                                    isCustomDateRange={localIsCustomDateRange}
                                    onCustomDateRangeChange={handleCustomDateRangeChange}
                                />
                            </div>
                        )}

                        {/* ── Grid Toolbar ─────────────────────────────────── */}
                        <div className="flex items-center justify-end gap-2 px-4 sm:pr-0 pb-3">
                            {/* Edit mode toggle */}
                            <button
                                onClick={() => setEditMode(v => !v)}
                                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                                    editMode
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                                }`}
                            >
                                <Move size={13} />
                                {editMode ? 'Done' : 'Rearrange'}
                            </button>

                            {/* Reset layout */}
                            <button
                                onClick={resetLayout}
                                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
                            >
                                <LayoutGrid size={13} />
                                Reset
                            </button>

                            {/* Add chart dropdown */}
                            <div className="relative" ref={addMenuRef}>
                                <button
                                    onClick={() => setAddMenuOpen(v => !v)}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border border-border bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                                >
                                    <Plus size={13} />
                                    Add Chart
                                    <ChevronDown size={11} className={`transition-transform ${addMenuOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {addMenuOpen && (
                                    <div className="absolute right-0 top-full mt-1.5 w-64 rounded-lg border border-border bg-popover shadow-xl z-50 overflow-hidden">
                                        {/* Category tabs */}
                                        <div className="flex border-b border-border">
                                            {['Operations', 'Circularity'].map(cat => (
                                                <button
                                                    key={cat}
                                                    onClick={() => setAddMenuCategory(cat)}
                                                    className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                                                        addMenuCategory === cat
                                                            ? 'bg-muted text-foreground'
                                                            : 'text-muted-foreground hover:text-foreground'
                                                    }`}
                                                >
                                                    {cat}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Chart list */}
                                        <div className="max-h-64 overflow-y-auto py-1">
                                            {filteredAvailable.length === 0 ? (
                                                <p className="px-4 py-3 text-xs text-muted-foreground italic">
                                                    All {addMenuCategory} charts are on the dashboard.
                                                </p>
                                            ) : (
                                                filteredAvailable.map(id => (
                                                    <button
                                                        key={id}
                                                        onClick={() => addChart(id)}
                                                        className="w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors flex items-center justify-between group"
                                                    >
                                                        <span>{CHART_META[id]?.title ?? id}</span>
                                                        <Plus size={13} className="text-muted-foreground group-hover:text-primary transition-colors" />
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Responsive Grid Layout ────────────────────────── */}
                        <ResponsiveGridLayout
                            className="layout"
                            layouts={gridLayouts}
                            onLayoutChange={onLayoutChange}
                            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                            rowHeight={120}
                            draggableHandle=".drag-handle"
                            isDraggable={editMode}
                            isResizable={editMode}
                            margin={[12, 12]}
                        >
                            {activeCharts.map(chartId => (
                                <div
                                    key={chartId}
                                    className="sm:rounded-lg border bg-card shadow-sm overflow-hidden flex flex-col relative group"
                                >
                                    {/* Edit mode overlay controls */}
                                    {editMode && (
                                        <div className="absolute inset-x-0 top-0 h-8 flex items-center justify-between px-2 bg-muted/90 backdrop-blur-sm z-10 rounded-t-lg">
                                            <span className="drag-handle cursor-move flex items-center gap-1 text-xs text-muted-foreground select-none">
                                                <Move size={12} />
                                                {CHART_META[chartId]?.title ?? chartId}
                                            </span>
                                            <button
                                                onClick={() => removeChart(chartId)}
                                                className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:bg-destructive/20 hover:text-destructive transition-colors"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    )}

                                    {/* Chart content */}
                                    <div className={`flex-1 w-full h-full overflow-hidden ${editMode ? 'pt-8 pointer-events-none' : ''}`}>
                                        {renderChart(chartId)}
                                    </div>
                                </div>
                            ))}
                        </ResponsiveGridLayout>

                    </div>
                </div>
            </div>
        </DashboardExport>
    );
}

// ─── Small helper ──────────────────────────────────────────────────────────────
function PlaceholderMsg({ msg }) {
    return (
        <div className="flex items-center justify-center h-full w-full text-xs text-muted-foreground italic px-4 text-center">
            {msg}
        </div>
    );
}